if (!customElements.get('mf-product-zones')) {
  class MFProductZones extends HTMLElement {
    connectedCallback() {
      this.variants = this.querySelector('product-variants[data-main-product-variants]');
      this.confidenceRail = this.querySelector('[data-mf-confidence-rail]');
      this.scheme = this.querySelector('[data-mf-scheme]');
      this.schemeImage = this.querySelector('[data-mf-scheme-image]');
      this.variantGallery = this.querySelector('[data-mf-variant-gallery]');
      this.variantGalleryImage = this.querySelector('[data-mf-variant-gallery-image]');
      this.variantGalleryZoom = this.querySelector('[data-mf-variant-gallery-zoom]');
      this.productPage = this.closest('product-page');
      this.gallerySlider = this.querySelector('[data-js-product-gallery] css-slider');
      this.galleryStatus = this.querySelector('[data-mf-gallery-status]');
      this.galleryThumbnails = [...this.querySelectorAll('.product-gallery__thumbnails .thumbnail')];
      this.inventory = this.querySelector('[data-mf-inventory]');
      this.inventoryMessage = this.querySelector('[data-mf-inventory-message]');
      this.schemeData = this.parseJSON('[data-mf-scheme-data]');
      this.inventoryData = this.parseJSON('[data-mf-inventory-data]');
      this.boundVariantChange = this.onVariantChange.bind(this);
      this.boundVariantInputChange = this.onVariantInputChange.bind(this);
      this.boundDisclosureToggle = this.onDisclosureToggle.bind(this);
      this.boundGalleryThumbnailClick = this.hideGalleryVariant.bind(this);
      this.disclosures = [...this.querySelectorAll('details[data-mf-disclosure-group]')];
      this.disclosures.forEach((disclosure) => {
        disclosure.addEventListener('toggle', this.boundDisclosureToggle);
      });
      this.galleryThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', this.boundGalleryThumbnailClick);
      });
      this.addEventListener('change', this.boundVariantInputChange, true);
      this.manualVariantSelection = false;

      if (this.variants) {
        this.variants.addEventListener('VARIANT_CHANGE', this.boundVariantChange);
        const initialVariantId = this.dataset.mfInitialVariantId;
        const initialVariant = initialVariantId ? { id: initialVariantId } : null;
        this.renderVariant(this.variants.currentVariant || initialVariant, false);
        customElements.whenDefined('product-page').then(() => {
          if (!this.isConnected) return;
          this.initialRenderFrame = requestAnimationFrame(() => {
            this.renderVariant(this.variants.currentVariant || initialVariant, false);
          });
        });
      } else {
        this.syncConfidenceRail();
      }
    }

    disconnectedCallback() {
      if (this.variants && this.boundVariantChange) {
        this.variants.removeEventListener('VARIANT_CHANGE', this.boundVariantChange);
      }
      if (this.initialRenderFrame) {
        cancelAnimationFrame(this.initialRenderFrame);
      }

      this.disclosures?.forEach((disclosure) => {
        disclosure.removeEventListener('toggle', this.boundDisclosureToggle);
      });
      this.galleryThumbnails?.forEach((thumbnail) => {
        thumbnail.removeEventListener('click', this.boundGalleryThumbnailClick);
      });
      this.removeEventListener('change', this.boundVariantInputChange, true);
    }

    parseJSON(selector) {
      const node = this.querySelector(selector);
      if (!node) return {};

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error(`Invalid MF product JSON: ${selector}`, error);
        return {};
      }
    }

    onVariantChange() {
      const variant = this.variants?.currentVariant || null;
      this.renderVariant(variant, this.manualVariantSelection);
    }

    onVariantInputChange(event) {
      if (this.variants?.contains(event.target)) {
        this.manualVariantSelection = true;
      }
    }

    renderVariant(variant, showVariantGallery = false) {
      if (showVariantGallery) {
        this.selectVariantInterior(variant);
      } else {
        this.hideGalleryVariant();
      }
      this.renderScheme(variant);
      this.renderInventory(variant);
    }

    selectVariantInterior(variant) {
      const data = variant ? this.schemeData[String(variant.id)] : null;
      const galleryIndex = Number(data?.gallery_index);
      const galleryItemCount = this.querySelectorAll('.product-gallery-item').length;

      if (!Number.isInteger(galleryIndex) || galleryIndex < 0 || galleryIndex >= galleryItemCount) return false;
      if (!this.productPage || typeof this.productPage.selectGalleryMedia !== 'function') return false;

      this.hideGalleryVariant();
      this.productPage.selectGalleryMedia(galleryIndex);
      return true;
    }

    hideGalleryVariant() {
      if (!this.variantGallery) return;

      this.variantGallery.hidden = true;
      this.variantGallery.removeAttribute('aria-label');

      if (this.variantGalleryZoom) {
        delete this.variantGalleryZoom.dataset.image;
        delete this.variantGalleryZoom.dataset.imageAlt;
      }

      if (this.gallerySlider) {
        this.gallerySlider.inert = false;
        this.gallerySlider.removeAttribute('aria-hidden');
      }
    }

    onDisclosureToggle(event) {
      if (!event.target.open) return;
      this.closeDisclosureSiblings(event.target);
    }

    getDisclosureGroup(disclosure) {
      if (disclosure.dataset.mfDisclosureGroup) return disclosure.dataset.mfDisclosureGroup;
      return null;
    }

    closeDisclosureSiblings(disclosure) {
      const group = this.getDisclosureGroup(disclosure);
      if (!group) return;

      this.querySelectorAll('details[open]').forEach((candidate) => {
        if (candidate !== disclosure && this.getDisclosureGroup(candidate) === group) {
          candidate.open = false;
        }
      });
    }

    renderScheme(variant) {
      if (!this.scheme || !this.schemeImage) return;

      const data = variant ? this.schemeData[String(variant.id)] : null;
      const visible = Boolean(data && data.src);
      this.scheme.hidden = !visible;

      if (!visible) {
        this.schemeImage.removeAttribute('src');
        this.schemeImage.removeAttribute('width');
        this.schemeImage.removeAttribute('height');
        this.schemeImage.alt = '';
        this.syncConfidenceRail();
        return;
      }

      this.schemeImage.src = data.src;
      this.schemeImage.width = Number(data.width);
      this.schemeImage.height = Number(data.height);
      this.schemeImage.alt = data.alt || '';
      this.syncConfidenceRail();
    }

    syncConfidenceRail() {
      if (!this.confidenceRail) return;

      const hasVisibleScheme = Boolean(this.scheme && !this.scheme.hidden);
      this.confidenceRail.dataset.mfHasScheme = String(hasVisibleScheme);
      const hasVisibleModule = [...this.confidenceRail.children]
        .some((node) => node.matches('[data-mf-zone]') && !node.hidden);
      this.confidenceRail.hidden = !hasVisibleModule;
    }

    renderInventory(variant) {
      if (!this.inventory || !this.inventoryMessage) return;

      const data = variant ? this.inventoryData[String(variant.id)] : null;
      if (!data) {
        this.inventoryMessage.textContent = '';
        return;
      }

      if (!data.available && data.policy !== 'continue') {
        this.inventoryMessage.textContent = this.inventory.dataset.soldOutTemplate;
      } else if (data.management === 'shopify' && Number(data.quantity) > 0) {
        this.inventoryMessage.textContent = this.inventory.dataset.inStockTemplate
          .replace('__MF_COUNT__', String(data.quantity));
      } else {
        this.inventoryMessage.textContent = this.inventory.dataset.orderTemplate;
      }
    }
  }

  customElements.define('mf-product-zones', MFProductZones);
}
